import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { toast } from "sonner";
import { ArrowLeft, BellRing } from "lucide-react";
import api from "@/lib/api";
import { PageLoader, useMinLoading } from "@/components/Preloader";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  Flag, LevelChip, InfoTip, AreaSpark, PANEL, PANEL_STYLE, CARD_STYLE,
  STAGE_META, IMPACT_META,
} from "@/components/home/regmap/regShared";

const SENT_COLOR = (score: number) =>
  score >= 40 ? "#22d38f" : score >= 10 ? "#14cfe6" : score > -10 ? "#c3c9d8" : score > -40 ? "#8b5cff" : "#f33db4";

const SentimentSlider = ({ score }: { score: number }) => {
  const pct = Math.min(Math.max((score + 100) / 2, 3), 97);
  return (
    <div className="relative mt-2.5" role="img" aria-label={`Regulatory coverage tone score ${score} on a −100 to +100 scale`}>
      <div className="h-[6px] rounded-full opacity-90"
        style={{ background: "linear-gradient(90deg,#f33db4 0%,#8b5cff 35%,#14cfe6 70%,#22d38f 100%)" }} />
      <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-[12px] h-[12px] rounded-full bg-white ring-2 ring-black/70 shadow-[0_0_10px_rgba(255,255,255,0.55)]"
        style={{ left: `${pct}%` }} aria-hidden />
    </div>
  );
};

const Metric = ({ label, value, sub, color = "#f2f5fc" }: { label: string; value: string | number; sub?: string; color?: string }) => (
  <div className="rounded-xl p-4" style={CARD_STYLE}>
    <p className="nl-geist font-bold text-[9px] tracking-[0.08em] text-[#8b93a9]">{label}</p>
    <p className="nl-sg font-medium text-[26px] leading-none mt-2" style={{ color }}>{value}</p>
    {sub && <p className="nl-geist text-[10px] text-[#6f7a94] mt-1.5">{sub}</p>}
  </div>
);

const SubscribeCard = ({ name, code }: { name: string; code: string }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/regulation/alerts/subscribe", { email, code });
      if (data.status === "subscribed") {
        toast.success(`Instant ${name} alerts are on — check your inbox.`);
        setEmail("");
      } else if (data.status === "already_subscribed") {
        toast.info(`You're already subscribed to ${name} alerts.`);
      } else {
        toast.error("Please enter a valid email address.");
      }
    } catch {
      toast.error("Subscription failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`${PANEL} p-5`} data-testid="jurisdiction-subscribe-card"
      style={{ background: "linear-gradient(135deg, #0e081c 0%, #060609 60%)", borderColor: "rgba(139,92,255,0.45)" }}>
      <p className="nl-geist font-bold text-[12px] text-[#eef1f9] inline-flex items-center gap-2">
        <BellRing className="w-3.5 h-3.5 text-[#b88cff]" aria-hidden /> INSTANT ALERTS
      </p>
      <p className="nl-geist text-[12px] text-[#9aa3b8] mt-2 leading-relaxed">
        Get an email the moment NewsLayer verifies a new regulatory development for {name}.
      </p>
      <form onSubmit={submit} className="mt-3.5 flex gap-2">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com" data-testid="jurisdiction-subscribe-email"
          className="flex-1 min-w-0 h-9 rounded-lg bg-[#08080d] ring-1 ring-white/[0.12] px-3 nl-geist text-[12px] text-white placeholder:text-[#5b6478] focus:outline-none focus:ring-[#8b5cff]" />
        <button type="submit" disabled={busy} data-testid="jurisdiction-subscribe-btn"
          className="h-9 px-4 rounded-lg bg-gradient-to-r from-[#8b5cff] to-[#6d3df0] nl-geist font-bold text-[11px] text-white hover:brightness-110 disabled:opacity-50 motion-safe:transition-[filter]">
          {busy ? "…" : "Subscribe"}
        </button>
      </form>
      <p className="nl-geist text-[9px] text-[#5b6478] mt-2.5">Free · unsubscribe in one click from any email.</p>
    </div>
  );
};

export default function JurisdictionProfile() {
  const { code } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["jurisdiction-profile", code],
    queryFn: async () => (await api.get(`/regulation/jurisdiction/${code}`)).data,
    enabled: !!code,
    staleTime: 5 * 60 * 1000,
  });
  const showLoader = useMinLoading(isLoading);
  if (showLoader) return <PageLoader />;
  if (isError || !data) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 text-center" data-testid="jurisdiction-not-found">
        <p className="nl-geist text-[14px] text-[#9aa3b8]">Unknown jurisdiction.</p>
        <Link to="/" className="nl-geist text-[12px] text-[#b88cff] hover:text-white mt-3 inline-block">← Back to NewsLayer</Link>
      </div>
    );
  }
  const j = data.jurisdiction;
  const name: string = data.name;
  const jcode: string = data.code;
  const histPts = (j?.sentiment_history || []).filter((p: any) => p.v != null).map((p: any) => p.v);
  return (
    <TooltipProvider delayDuration={150}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="jurisdiction-profile-page">
        <Link to="/" className="nl-geist text-[11px] text-[#7a86a0] hover:text-white inline-flex items-center gap-1.5 motion-safe:transition-colors" data-testid="jurisdiction-back-link">
          <ArrowLeft className="w-3.5 h-3.5" aria-hidden /> Regulation Intelligence
        </Link>

        <header className="mt-5 flex flex-wrap items-center gap-4">
          <Flag code={jcode} className="w-[52px] h-[36px]" />
          <div className="min-w-0">
            <h1 className="text-[#f2f5fc] font-bold text-[28px] leading-[34px] sm:text-[36px] sm:leading-[42px] tracking-tight"
              style={{ fontFamily: "Inter, 'Helvetica Neue', Helvetica, Arial, sans-serif" }} data-testid="jurisdiction-name">
              {name}
            </h1>
            <p className="nl-geist text-[12px] text-[#8b93a9] mt-1">Jurisdiction regulatory profile · last 60 days of verified coverage</p>
          </div>
          {j && <LevelChip level={j.level} className="ml-auto" />}
        </header>

        <div className="mt-7 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="jurisdiction-metrics">
          <Metric label="POLICY STORIES · 30D" value={j?.stories_30d ?? data.stories_60d}
            sub={j ? `${j.delta_30d >= 0 ? "+" : ""}${j.delta_30d} vs prior 30D` : "60-day total"} />
          <Metric label="ENFORCEMENT STORIES · 30D" value={j?.enforcement_30d ?? "—"}
            sub={j?.enforcement_delta_pct != null ? `${j.enforcement_delta_pct > 0 ? "+" : ""}${j.enforcement_delta_pct}% vs prior` : "Derived from coverage"} color="#14cfe6" />
          <Metric label="POLICY MOMENTUM" value={j?.momentum ?? "—"} sub="7D vs prior 7D" color="#b88cff" />
          <Metric label="TRACKED INITIATIVES" value={j?.initiatives || (data.pipeline_rows?.length ?? 0) || "—"} sub="Active in pipeline" color="#f5b420" />
        </div>

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-[minmax(0,62fr)_minmax(0,38fr)] gap-5">
          <div className="space-y-5 min-w-0">
            <section className={`${PANEL} p-5`} style={PANEL_STYLE} data-testid="jurisdiction-alerts">
              <h2 className="nl-geist font-bold text-[13px] text-[#eef1f9] tracking-[0.04em]">LATEST REGULATORY ALERTS</h2>
              {data.alerts?.length ? (
                <ul className="mt-4 space-y-2.5">
                  {data.alerts.map((a: any) => (
                    <li key={a.slug}>
                      <Link to={`/article/${a.slug}`} data-testid={`jurisdiction-alert-${a.slug}`}
                        className="block rounded-xl ring-1 ring-white/[0.08] p-3.5 hover:ring-[#8b5cff]/70 motion-safe:transition-shadow" style={CARD_STYLE}>
                        <div className="flex items-center gap-2">
                          <span className="nl-geist font-bold text-[8.5px] tracking-[0.08em] text-[#b88cff] uppercase">{a.kind}</span>
                          <span className="nl-geist text-[9px] text-[#5b6478] ml-auto shrink-0">{a.published_at ? dayjs(a.published_at).fromNow() : ""}</span>
                        </div>
                        <p className="nl-geist font-semibold text-[13px] leading-snug text-[#e8ecf6] mt-1.5">{a.title}</p>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="nl-geist text-[12px] text-[#6f7a94] mt-4">No qualifying regulatory alerts in the last 60 days.</p>
              )}
            </section>

            {data.stories?.length > 0 && (
              <section className={`${PANEL} p-5`} style={PANEL_STYLE} data-testid="jurisdiction-stories">
                <h2 className="nl-geist font-bold text-[13px] text-[#eef1f9] tracking-[0.04em]">RECENT POLICY COVERAGE</h2>
                <ul className="mt-3 divide-y divide-white/[0.06]">
                  {data.stories.map((s: any) => (
                    <li key={s.slug}>
                      <Link to={`/article/${s.slug}`} className="flex items-baseline gap-3 py-2.5 group" data-testid={`jurisdiction-story-${s.slug}`}>
                        <span className="nl-geist text-[12px] text-[#c9cfdd] group-hover:text-white leading-snug flex-1 motion-safe:transition-colors">{s.title}</span>
                        <span className="nl-geist text-[9px] text-[#5b6478] shrink-0">{s.published_at ? dayjs(s.published_at).fromNow() : ""}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>

          <div className="space-y-5 min-w-0">
            <section className={`${PANEL} p-5`} style={PANEL_STYLE} data-testid="jurisdiction-sentiment">
              <div className="flex items-center gap-1.5">
                <h2 className="nl-geist font-bold text-[13px] text-[#eef1f9] tracking-[0.04em]">REGULATORY COVERAGE TONE</h2>
                <InfoTip label="Coverage tone methodology">
                  Deterministic tone score of NewsLayer's verified policy coverage for this
                  jurisdiction over the last 30 days. Each story is lexicon-scored for regulatory
                  posture (supportive vs restrictive); the net balance maps to −100…+100. Requires
                  at least 3 scored stories. Not an official government metric and not market sentiment.
                </InfoTip>
              </div>
              {j?.sentiment ? (
                <>
                  <p className="nl-sg font-medium text-[30px] leading-none mt-3" style={{ color: SENT_COLOR(j.sentiment.score) }} data-testid="jurisdiction-sentiment-label">
                    {j.sentiment.label}
                  </p>
                  <SentimentSlider score={j.sentiment.score} />
                  <p className="nl-geist text-[9.5px] text-[#6f7a94] mt-2">
                    {j.sentiment.coverage_stories} scored policy stories · last 30 days
                  </p>
                  {histPts.length >= 2 && (
                    <div className="mt-4">
                      <AreaSpark points={histPts} color={SENT_COLOR(j.sentiment.score)} className="w-full h-12" />
                      <p className="nl-geist text-[8.5px] text-[#5b6478] mt-1">30-day coverage tone trend</p>
                    </div>
                  )}
                </>
              ) : (
                <p className="nl-geist text-[12px] text-[#6f7a94] mt-3">Not enough scored coverage yet for a verified tone reading.</p>
              )}
            </section>

            <SubscribeCard name={name} code={jcode} />

            <section className={`${PANEL} p-5`} style={PANEL_STYLE} data-testid="jurisdiction-pipeline">
              <h2 className="nl-geist font-bold text-[13px] text-[#eef1f9] tracking-[0.04em]">REGULATORY PIPELINE</h2>
              {data.pipeline_rows?.length ? (
                <ul className="mt-3 space-y-2.5">
                  {data.pipeline_rows.map((r: any) => {
                    const stage = STAGE_META[r.stage] || STAGE_META.in_review;
                    const impact = r.impact ? IMPACT_META[r.impact] : null;
                    return (
                      <li key={r.slug} className="rounded-xl ring-1 ring-white/[0.08] p-3.5" style={CARD_STYLE}>
                        <div className="flex items-center gap-2">
                          <span className="nl-geist font-bold text-[8.5px] tracking-[0.06em] uppercase px-1.5 py-0.5 rounded"
                            style={{ color: stage.color, background: `${stage.color}1f` }}>{stage.label}</span>
                          {impact && <span className="nl-geist text-[8.5px] font-bold uppercase" style={{ color: impact.color }}>{impact.label}</span>}
                          <span className="nl-geist text-[9px] text-[#5b6478] ml-auto shrink-0">{r.updated_at ? dayjs(r.updated_at).fromNow() : ""}</span>
                        </div>
                        <Link to={`/regulation/${r.slug}`} className="block nl-geist font-semibold text-[12.5px] leading-snug text-[#e8ecf6] hover:text-white mt-1.5 motion-safe:transition-colors">
                          {r.title}
                        </Link>
                        {r.summary && <p className="nl-geist text-[10.5px] text-[#8b93a9] mt-1 leading-relaxed">{r.summary}</p>}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="nl-geist text-[12px] text-[#6f7a94] mt-3">No tracked regulation records for this jurisdiction yet.</p>
              )}
              <Link to={`/regulation-tracker?country=${encodeURIComponent(name)}`} data-testid="jurisdiction-tracker-link"
                className="inline-block nl-geist font-semibold text-[10.5px] text-[#14cfe6] hover:text-white mt-3.5 motion-safe:transition-colors">
                Open in Regulation Tracker →
              </Link>
            </section>
          </div>
        </div>

        <p className="nl-geist text-[9px] text-[#5b6478] mt-6 text-center">
          Metrics are derived from NewsLayer policy coverage and tracked regulation records — not official government statistics.
        </p>
      </div>
    </TooltipProvider>
  );
}
